import { useCallback, useState } from 'react';
import { formatCurrency } from '@clickersphere/core/utils/formatCurrency';

export interface ISelectedItem {
  id: string;
  quantity: number;
}

interface IPricedItem {
  id: string;
  price: number;
}

interface IUseSelectableItemsResult {
  selectedItems: ISelectedItem[];
  handleSelectionChange: (ids: string[]) => void;
  handleQuantityChange: (id: string, quantity: number) => void;
  handleRemove: (id: string) => void;
  getSubtotal: (id: string, quantity: number) => string;
}

export function useSelectableItems(
  source: IPricedItem[] | undefined,
  language: string,
  initialItems: ISelectedItem[] = [],
): IUseSelectableItemsResult {
  const [selectedItems, setSelectedItems] =
    useState<ISelectedItem[]>(initialItems);

  const handleSelectionChange = useCallback((ids: string[]) => {
    setSelectedItems((prev) =>
      ids.map((id) => {
        const existing = prev.find((item) => item.id === id);
        return existing ?? { id, quantity: 1 };
      }),
    );
  }, []);

  const handleQuantityChange = useCallback((id: string, quantity: number) => {
    setSelectedItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, quantity } : item)),
    );
  }, []);

  const handleRemove = useCallback((id: string) => {
    setSelectedItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const getSubtotal = useCallback(
    (id: string, quantity: number): string => {
      const item = source?.find((candidate) => candidate.id === id);
      if (!item) return formatCurrency(0, language);
      return formatCurrency(item.price * quantity, language);
    },
    [source, language],
  );

  return {
    selectedItems,
    handleSelectionChange,
    handleQuantityChange,
    handleRemove,
    getSubtotal,
  };
}
